import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import styled from '@emotion/styled';
import Spinner from 'components/spinner';
import moment from 'moment';
import { each, keys } from 'lodash';
import { AppUI } from '@seedlang/state';
import autobind from 'autobind-decorator';
import { Constants } from '@seedlang/constants';
import { isPresent, isBlank } from '@seedlang/utils';
import AnalyticsDateModal from 'components/analytics/analytics_date_modal';
import AnalyticsDayAnalysis from 'components/analytics/analytics_day_analysis';
import AnalyticsList from 'components/analytics/analytics_list';
import Modal from 'components/modal';
import InPlaceSelect from 'components/form/in_place_select';
import { buttonColumnWidth, dateColumnWidth } from 'components/analytics/analytics_list_row';
import { Header, Wrapper, CalendarWrapper, IconWrapper, DateString } from 'components/analytics/analytics_default';

const TabsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  margin-left: auto;
  align-self: end;
`;

const Tab = styled.span`
  display: block;
  padding: 0 12px;
  border-radius: 8px 8px 0 0;
  font-size: 14px;
  background: ${props => props.isSelected ? 'white' : '#CCC'};
  cursor: pointer;
`;

const tabs = [
  { id: 'overview', name: 'Overview' },
  { id: 'tiers', name: 'Tiers' },
  { id: 'active', name: 'Active Users' },
];

@observer
class AnalyticsCreator extends React.Component {
  @observable loading = true;
  @observable counts = {};
  @observable stats = {};
  @observable selectedDay;
  @observable selectedMonth;
  @observable selectedYear;
  @observable selectedTab = tabs[0];
  @observable showDateModal = false;
  @observable timePeriod;
  @observable activeFields;

  constructor(props) {
    super(props);
    this.timePeriod = this.getCookieValue('timePeriod') || 'daily';
    if (window.location.href.match('month')) { // for some reason props.params is not including month and date?
      const ary = window.location.href.split('/year/')[1].split('/month/');
      this.selectedMonth = parseInt(ary[1], 10);
      this.selectedYear = parseInt(ary[0], 10);
    } else {
      const todaysDate = new Date();
      this.selectedMonth = parseInt(moment(todaysDate).format('M'), 10);
      this.selectedYear = parseInt(moment(todaysDate).format('Y'), 10);
    }
    (async () => {
      this.loading = true;
      await this.loadMembershipTypes();
      this.activeFields = isPresent(this.getCookieValue('activeFields')) ? this.getCookieValue('activeFields').split(',') : keys(this.possibleFields);
      this.getAnalyticsIndex();
    })();
  }

  @computed get languageId() {
    return AppUI.site?.languageId;
  }

  @computed get possibleFields() {
    const fields = {};

    // Overview tab
    fields.newSubscriptionsCount = { name: 'New Subscriptions', tabId: 'overview' };
    fields.cancellationsCount = { name: 'New Cancellations', tabId: 'overview' };
    fields.totalSubscriptionsCount = { name: 'Total Active Subscriptions', tabId: 'overview' };

    // Tiers tab
    const membershipTypes = AppUI.membershipTypeStore.indexData.slice().sort((a, b) => a.position - b.position);
    membershipTypes.forEach(({name, position}) => {
      fields[`newSubscriptionsCountTier${position}`] = {
        name: `${shortenTierName(name)} (new)`,
        tooltip: `${name} – New Subscriptions`,
        tabId: 'tiers',
      };
      fields[`totalSubscriptionsCountTier${position}`] = {
        name: `${shortenTierName(name)} (total)`,
        tooltip: `${name} – Total Active Subscriptions`,
        tabId: 'tiers',
      };
    });

    // Active Users tab
    const activeUsersField = (period, periodLy) => (
      {
        name: `${periodLy} Active Users`,
        tooltip: `Number of people per ${period} that interacted with a worksheet`,
        tabId: 'active',
      }
    );
    fields.dailyActiveUsersCount = activeUsersField('day', 'Daily');
    fields.weeklyActiveUsersCount = activeUsersField('week', 'Weekly');
    fields.monthlyActiveUsersCount = activeUsersField('month', 'Monthly');

    return fields;
  }

  @computed get selectedDateString() {
    const selectedDayWithZero = String(this.selectedDay).padStart(2, '0');
    const selectedMonthWithZero = String(this.selectedMonth).padStart(2, '0');
    return `${this.selectedYear}-${selectedMonthWithZero}-${selectedDayWithZero}`;
  }

  @computed get fromDate() {
    const month = this.timePeriod === 'daily' ? this.selectedMonth - 1 : 0;
    return new Date(this.selectedYear, month, 1);
  }

  @computed get toDate() {
    const month = this.timePeriod === 'daily' ? this.selectedMonth : 11;
    return new Date(this.selectedYear, month, 31);
  }

  @computed get visibleFieldsWithDetails() {
    const activeFieldsWithDetails = keys(this.possibleFields).filter(fieldKey => this.activeFields.includes(fieldKey)).map(fieldKey => [fieldKey, this.possibleFields[fieldKey]]);
    return activeFieldsWithDetails.filter(([, fieldDetails]) => fieldDetails.tabId === null || fieldDetails.tabId === this.selectedTab?.id);
  }

  @autobind afterGetAnalyticsIndex(resp) {
    this.loading = false;
    if (isBlank(resp) && !Array.isArray(resp)) { return; }
    this.counts = resp;
    each(this.counts, item => {
      AppUI.membershipTypeStore.indexData.sort((a, b) => a.position - b.position).forEach(({ id, position }) => {
        item[`newSubscriptionsCountTier${position}`] = item.newSubscriptionsCountsByTier.find(({membershipTypeId}) => membershipTypeId === id)?.count;
        item[`totalSubscriptionsCountTier${position}`] = item.totalSubscriptionsCountsByTier.find(({membershipTypeId}) => membershipTypeId === id)?.count;
      });
      delete item.newSubscriptionsCountsByTier;
      delete item.totalSubscriptionsCountsByTier;
    });
    this.stats[this.languageId] = {};
    each(['newUsersCount', 'newSubscriptionsCount'], type => {
      this.stats[this.languageId][type] = {max: 0, sum: 0, count: 0, average: 0};
      this.counts.filter(item => item.languageId === this.languageId).map(result => {
        this.stats[this.languageId][type]['count'] += 1;
        this.stats[this.languageId][type]['sum'] += result[type];
        this.stats[this.languageId][type]['average'] = parseInt(this.stats[this.languageId][type]['sum'] / this.stats[this.languageId][type]['count'], 10);
      });
    });
  }

  @autobind changeDate(incrementOrDecrement) {
    const value = incrementOrDecrement === 'increment' ? 1 : -1;
    if (this.timePeriod === 'daily') {
      const date = new Date(this.selectedYear, this.selectedMonth - 1 + value, 1);
      this.selectedYear = date.getFullYear();
      this.selectedMonth = date.getMonth() + 1;
    } else {
      this.selectedYear = this.selectedYear + value;
    }
    AppUI.routeStore.routeToNamed('creator.analytics', { month: this.selectedMonth, year: this.selectedYear});
    this.getAnalyticsIndex();
  }

  @autobind getAnalyticsIndex() {
    this.loading = true;
    AppUI.analyticStore.getIndex({queryStrings: {white_label: true}, filters: {
      time_period: this.timePeriod,
      date: `RANGE[${this.fromDate.toISOString()},${this.toDate.toISOString()}]`,
    }}, this.afterGetAnalyticsIndex);
  }

  @autobind loadMembershipTypes() {
    return new Promise(resolve => {
      if (AppUI.membershipTypeStore.hasIndexData) resolve();
      AppUI.membershipTypeStore.getIndex({}, resp => {
        AppUI.membershipTypeStore.afterGetIndex(resp);
        resolve();
      });
    });
  }

  @autobind onSelectDate(year, month) {
    this.selectedYear = year;
    this.selectedMonth = month;
    this.showDateModal = false;
    AppUI.routeStore.routeToNamed('creator.analytics', {month: this.selectedMonth, year: this.selectedYear});
    this.getAnalyticsIndex();
  }

  @autobind onSelectDay(day) {
    this.selectedDay = day;
  }

  @autobind onCloseAnalysisModal() {
    this.selectedDay = null;
    AppUI.userSubscriptionStore.clearIndexData();
    AppUI.subscriptionStore.clearIndexData();
  }

  @autobind onSetTimePeriod(timePeriod) {
    this.timePeriod = timePeriod;
    this.setCookieValue('timePeriod', this.timePeriod);
    this.getAnalyticsIndex();
  }

  @autobind getCookieValue(field) {
    return AppUI.getCookieValue(`analytics-white-label-${field}`);
  }

  @autobind setCookieValue(field, value) {
    AppUI.setCookieValue(`analytics-white-label-${field}`, value);
  }

  render() {
    return (
      <Wrapper>
        {
          this.loading &&
            <Spinner />
        }
        {
          this.showDateModal &&
            <Modal
              width='400px'
              height='250px'
              hideCloseIcon
              onCloseModal={() => this.showDateModal = false}
            >
              <AnalyticsDateModal
                onSelectDate={this.onSelectDate}
                year={this.selectedYear}
              />
            </Modal>
        }
        {
          this.selectedDay && isPresent(this.counts) &&
            <Modal
              width='800px'
              height='700px'
              onCloseModal={this.onCloseAnalysisModal}
              hideCloseIcon
            >
              <AnalyticsDayAnalysis
                whiteLabel
                languageIds={[this.languageId]}
                activeLanguageIds={[this.languageId]}
                dateString={this.selectedDateString}
                counts={this.counts}
                month={this.selectedMonth}
                year={this.selectedYear}
                day={this.selectedDay}
              />
            </Modal>
        }
        {
          !this.loading &&
            <CalendarWrapper width='100%'>
              <Header>
                <IconWrapper
                  fontSize='28px'
                  onClick={() => this.changeDate('decrement')}
                >
                  <i className='fa fa-angle-left' />
                </IconWrapper>
                <IconWrapper
                  fontSize='28px'
                  onClick={() => this.changeDate('increment')}
                >
                  <i className='fa fa-angle-right' />
                </IconWrapper>
                <InPlaceSelect
                  margin='0 5px 0 0'
                  options={[['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']]}
                  onChange={val => this.onSetTimePeriod(val)}
                  value={this.timePeriod}
                />
                {
                  this.timePeriod === 'daily' &&
                    <DateString
                      hoverBackground='#e0e0e0'
                      cursor='pointer'
                      onClick={() => this.showDateModal = true}
                    >
                      {Constants.MONTHS[this.selectedMonth - 1]} {this.selectedYear}
                    </DateString>
                }
                {
                  this.timePeriod !== 'daily' &&
                    <DateString
                      cursor='default'
                    >
                      {this.selectedYear}
                    </DateString>
                }
                <TabsContainer>{
                  tabs.map(tab => (
                    <Tab key={tab.id} isSelected={this.selectedTab?.id === tab.id} onClick={() => this.selectedTab = tab}>
                      {tab.name}
                    </Tab>
                  ))
                  }
                </TabsContainer>
              </Header>
              {
                isPresent(this.counts) &&
                  <AnalyticsList
                    stats={this.stats}
                    counts={this.counts}
                    timePeriod={this.timePeriod}
                    selectedMonth={this.selectedMonth}
                    selectedYear={this.selectedYear}
                    languageIds={[this.languageId]}
                    onSelectDay={this.onSelectDay}
                    statFields={this.visibleFieldsWithDetails}
                    dataColumnWidth={Math.trunc((999 - buttonColumnWidth - dateColumnWidth) / this.visibleFieldsWithDetails.length)}
                  />
                }
            </CalendarWrapper>
        }
      </Wrapper>
    );
  }
}

function shortenTierName(name) {
  return name.replace(/ Membership$/i, '').replaceAll(/ and /g, '&');
}

export default AnalyticsCreator;
